import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { colors, spacing, borderRadius, typography } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, apiDelete, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface Period {
  id: string; day_of_week: number; period_number: number;
  start_time: string; end_time: string; period_type: string; is_active: boolean;
}

interface Slot {
  id: string; class_id: string; subject_id: string | null; teacher_id: string | null;
  room_id: string | null; day_of_week: number; period_id: string;
  slot_type: string; is_substitute: boolean;
  start_time: string; end_time: string; period_number: number; period_type: string;
  room_name: string | null; room_code: string | null;
  class_name?: string; teacher_name?: string;
  term: string; version_status: string;
}

interface ConflictSlot { slotId: string; type: string; detail: string }

const DAYS_BN = ['Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
const DAYS_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const BN = {
  pageTitle: 'Grid Jadual Waktu',
  backLink: '← Senarai Versi',
  versionLabel: 'Versi',
  slotLabel: 'slot',
  conflictLabel: 'konflik',
  addSlotBtn: '+ Tambah Slot',
  closeFormBtn: 'Tutup Form',
  refreshBtn: '↻',
  addSlotFormTitle: '+ Tambah Slot Jadual',
  labelClassId: 'ID Kelas *',
  labelDay: 'Hari *',
  labelPeriod: 'Period *',
  labelTeacherId: 'ID Guru',
  labelRoomId: 'ID Bilik',
  labelSlotType: 'Jenis Slot',
  periodSelectPlaceholder: '-- Pilih --',
  daySelectSuffix: '(1=Isnin)',
  confirmAddBtn: 'Tambah Slot',
  addingBtn: 'Menambah…',
  cancelBtn: 'Batal',
  retryBtn: 'Cuba Lagi',
  emptyPeriods: 'Tiada period dikonfigurasi untuk sekolah ini.',
  conflictSectionTitle: 'Konflik Ditemui',
  substituteLabel: 'Ganti',
  periodColHeader: 'Period',
};

const EN = {
  pageTitle: 'Timetable Grid',
  backLink: '← Version List',
  versionLabel: 'Version',
  slotLabel: 'slots',
  conflictLabel: 'conflicts',
  addSlotBtn: '+ Add Slot',
  closeFormBtn: 'Close Form',
  refreshBtn: '↻',
  addSlotFormTitle: '+ Add Timetable Slot',
  labelClassId: 'Class ID *',
  labelDay: 'Day *',
  labelPeriod: 'Period *',
  labelTeacherId: 'Teacher ID',
  labelRoomId: 'Room ID',
  labelSlotType: 'Slot Type',
  periodSelectPlaceholder: '-- Select --',
  daySelectSuffix: '(1=Mon)',
  confirmAddBtn: 'Add Slot',
  addingBtn: 'Adding…',
  cancelBtn: 'Cancel',
  retryBtn: 'Retry',
  emptyPeriods: 'No periods configured for this school.',
  conflictSectionTitle: 'Conflicts Found',
  substituteLabel: 'Sub',
  periodColHeader: 'Period',
};

const PERIOD_TYPE_COLOR: Record<string, string> = {
  LESSON: colors.neutral.charcoal, BREAK: '#92400e', ASSEMBLY: '#1e40af',
  RECESS: '#92400e', PRAYER: '#064e3b', ACTIVITY: '#581c87',
};

const DEMO_SCHOOL_ID = '3f868b12-b554-4f51-a893-69e9b4ba9f57';

export function TimetableGridPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  const DAYS = lang === 'en' ? DAYS_EN : DAYS_BN;

  const { versionId } = useParams<{ versionId: string }>();
  const [searchParams] = useSearchParams();
  const viewMode = (searchParams.get('view') ?? 'class') as 'class' | 'teacher' | 'room';
  const viewId   = searchParams.get('id') ?? '';

  const schoolId = user.roles.find((r) => r.schoolId)?.schoolId ?? DEMO_SCHOOL_ID;

  const [periods,   setPeriods]   = useState<Period[]>([]);
  const [slots,     setSlots]     = useState<Slot[]>([]);
  const [conflicts, setConflicts] = useState<ConflictSlot[]>([]);

  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [toast,     setToast]     = useState<{ message: string; variant: ToastVariant } | null>(null);

  // Add slot form
  const [showAddSlot,  setShowAddSlot]  = useState(false);
  const [addForm,      setAddForm]      = useState({ classId: '', dayOfWeek: '1', periodId: '', teacherId: '', roomId: '', slotType: 'REGULAR' });
  const [addError,     setAddError]     = useState<string | null>(null);
  const [adding,       setAdding]       = useState(false);
  const [deletingId,   setDeletingId]   = useState<string | null>(null);

  const isAdmin = user.roles.some((r) => ['admin', 'principal', 'super_admin'].includes(r.role));

  const loadGrid = useCallback(async () => {
    if (!versionId) { setError('ID versi tidak sah. / Invalid version ID.'); return; }
    setLoading(true); setError(null);
    try {
      const [periodsData, gridData] = await Promise.all([
        apiGet<Period[]>(`/api/timetable/periods/${schoolId}`),
        viewMode === 'class'   ? apiGet<{ slots: Slot[] }>(`/api/timetable/view/class/${viewId || versionId}?versionId=${versionId}`) :
        viewMode === 'teacher' ? apiGet<{ slots: Slot[] }>(`/api/timetable/view/teacher/${viewId}?versionId=${versionId}`) :
                                 apiGet<{ slots: Slot[] }>(`/api/timetable/view/room/${viewId}?versionId=${versionId}`),
      ]);
      setPeriods(periodsData);
      setSlots((gridData as { slots: Slot[] }).slots);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan grid jadual.');
    } finally { setLoading(false); }
  }, [versionId, viewMode, viewId, schoolId]);

  useEffect(() => { void loadGrid(); }, [loadGrid]);

  async function handleAddSlot() {
    if (!addForm.classId.trim())  { setAddError('Sila masukkan ID kelas. / Enter class ID.'); return; }
    if (!addForm.periodId.trim()) { setAddError('Sila pilih period. / Select a period.'); return; }
    const dow = parseInt(addForm.dayOfWeek);
    if (isNaN(dow) || dow < 1 || dow > 6) { setAddError('Hari mesti antara 1–6 (Isnin–Sabtu). / Day must be 1–6 (Mon–Sat).'); return; }

    setAdding(true); setAddError(null);
    try {
      const result = await apiPost<{ id: string; conflicts: ConflictSlot[] }>(
        `/api/timetable/versions/${versionId}/slots`,
        {
          classId:    addForm.classId.trim(),
          dayOfWeek:  dow,
          periodId:   addForm.periodId.trim(),
          teacherId:  addForm.teacherId.trim() || undefined,
          roomId:     addForm.roomId.trim() || undefined,
          slotType:   addForm.slotType,
        },
      );
      if (result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        setToast({ variant: 'error', message: `Slot ditambah tetapi ${result.conflicts.length} konflik ditemui. / Slot added but ${result.conflicts.length} conflict(s) found.` });
      } else {
        setToast({ variant: 'success', message: 'Slot ditambah tanpa konflik. / Slot added with no conflicts.' });
      }
      setShowAddSlot(false);
      setAddForm({ classId: '', dayOfWeek: '1', periodId: '', teacherId: '', roomId: '', slotType: 'REGULAR' });
      void loadGrid();
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Gagal menambah slot.');
    } finally { setAdding(false); }
  }

  async function handleDeleteSlot(slotId: string) {
    setDeletingId(slotId);
    try {
      const result = await apiDelete<{ conflicts: ConflictSlot[] }>(`/api/timetable/slots/${slotId}`);
      if (result.conflicts.length > 0) {
        setConflicts(result.conflicts);
      } else {
        setConflicts([]);
      }
      setToast({ variant: 'success', message: 'Slot dipadam. / Slot deleted.' });
      void loadGrid();
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal memadam slot.' });
    } finally { setDeletingId(null); }
  }

  // Build grid: group slots by day then period
  const uniqueDays = [...new Set(periods.map((p) => p.day_of_week))].sort();
  const periodsByDay  = (day: number) => periods.filter((p) => p.day_of_week === day && p.is_active).sort((a, b) => a.period_number - b.period_number);

  const slotAt = (day: number, periodId: string) =>
    slots.filter((s) => s.day_of_week === day && s.period_id === periodId);

  const conflictIds = new Set(conflicts.map((c) => c.slotId));

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/admin/timetable" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>{UI.backLink}</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                {UI.pageTitle}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {UI.versionLabel} {versionId?.slice(0, 8)}… · {slots.length} {UI.slotLabel}
                {conflicts.length > 0 && <span style={{ color: '#f87171', marginLeft: spacing.sm }}>⚠ {conflicts.length} {UI.conflictLabel}</span>}
              </p>
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
              {isAdmin && (
                <Button variant="secondary" size="sm" onClick={() => setShowAddSlot(!showAddSlot)}>
                  {showAddSlot ? UI.closeFormBtn : UI.addSlotBtn}
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => void loadGrid()} disabled={loading}>
                {loading ? <Spinner size="sm" /> : UI.refreshBtn}
              </Button>
            </div>
          </div>

          {/* Add slot form */}
          {showAddSlot && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg, border: `1px solid ${colors.neutral.gray800}` }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, fontSize: typography.fontSize.sm }}>
                {UI.addSlotFormTitle}
              </p>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
                <div style={{ flex: '2 1 180px' }}>
                  <label style={labelSt}>{UI.labelClassId}</label>
                  <input type="text" value={addForm.classId}
                    onChange={(e) => setAddForm((f) => ({ ...f, classId: e.target.value }))}
                    placeholder="UUID kelas" disabled={adding} style={inputSt()} />
                </div>
                <div style={{ flex: '1 1 100px' }}>
                  <label style={labelSt}>{UI.labelDay} {UI.daySelectSuffix}</label>
                  <select value={addForm.dayOfWeek}
                    onChange={(e) => setAddForm((f) => ({ ...f, dayOfWeek: e.target.value }))}
                    disabled={adding} style={inputSt()}>
                    {DAYS.map((d, i) => <option key={i+1} value={i+1}>{d}</option>)}
                  </select>
                </div>
                <div style={{ flex: '2 1 180px' }}>
                  <label style={labelSt}>{UI.labelPeriod}</label>
                  <select value={addForm.periodId}
                    onChange={(e) => setAddForm((f) => ({ ...f, periodId: e.target.value }))}
                    disabled={adding} style={inputSt()}>
                    <option value="">{UI.periodSelectPlaceholder}</option>
                    {periodsByDay(parseInt(addForm.dayOfWeek)).map((p) => (
                      <option key={p.id} value={p.id}>P{p.period_number} {p.start_time}–{p.end_time}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
                <div style={{ flex: '2 1 180px' }}>
                  <label style={labelSt}>{UI.labelTeacherId}</label>
                  <input type="text" value={addForm.teacherId}
                    onChange={(e) => setAddForm((f) => ({ ...f, teacherId: e.target.value }))}
                    placeholder="UUID (pilihan)" disabled={adding} style={inputSt()} />
                </div>
                <div style={{ flex: '2 1 180px' }}>
                  <label style={labelSt}>{UI.labelRoomId}</label>
                  <input type="text" value={addForm.roomId}
                    onChange={(e) => setAddForm((f) => ({ ...f, roomId: e.target.value }))}
                    placeholder="UUID (pilihan)" disabled={adding} style={inputSt()} />
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <label style={labelSt}>{UI.labelSlotType}</label>
                  <select value={addForm.slotType}
                    onChange={(e) => setAddForm((f) => ({ ...f, slotType: e.target.value }))}
                    disabled={adding} style={inputSt()}>
                    {['REGULAR','DOUBLE','ELECTIVE','CCA'].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {addError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{addError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <Button variant="primary" size="sm" onClick={() => void handleAddSlot()} disabled={adding} loading={adding}>
                  {adding ? UI.addingBtn : UI.confirmAddBtn}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddSlot(false); setAddError(null); }} disabled={adding}>{UI.cancelBtn}</Button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
              <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{error}</p>
              <Button variant="ghost" size="sm" onClick={() => void loadGrid()} style={{ marginTop: spacing.xs }}>{UI.retryBtn}</Button>
            </div>
          )}

          {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>}

          {!loading && !error && periods.length === 0 && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
              <p style={{ margin: 0, color: colors.neutral.gray400 }}>
                {UI.emptyPeriods}
              </p>
            </div>
          )}

          {/* Timetable grid */}
          {!loading && periods.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr>
                    <th style={thStyle()}>{UI.periodColHeader}</th>
                    {uniqueDays.map((d) => (
                      <th key={d} style={thStyle()}>{DAYS[d - 1] ?? `${lang === 'en' ? 'Day' : 'Hari'} ${d}`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...new Set(periods.map((p) => p.period_number))].sort((a, b) => a - b).map((pNum) => (
                    <tr key={pNum}>
                      {(() => {
                        const sample = periods.find((p) => p.period_number === pNum);
                        return (
                          <td style={tdStyle()}>
                            <p style={{ margin: 0, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>P{pNum}</p>
                            <p style={{ margin: 0, fontSize: '10px', color: colors.neutral.gray400 }}>
                              {sample?.start_time}–{sample?.end_time}
                            </p>
                            <p style={{ margin: 0, fontSize: '10px', color: colors.neutral.gray600 }}>{sample?.period_type}</p>
                          </td>
                        );
                      })()}
                      {uniqueDays.map((d) => {
                        const period = periods.find((p) => p.day_of_week === d && p.period_number === pNum);
                        const daySlots = period ? slotAt(d, period.id) : [];
                        return (
                          <td key={d} style={{ ...tdStyle(), backgroundColor: period ? (PERIOD_TYPE_COLOR[period.period_type] ?? colors.neutral.charcoal) : colors.neutral.black }}>
                            {daySlots.length === 0 ? (
                              <span style={{ fontSize: '10px', color: colors.neutral.gray600 }}>—</span>
                            ) : (
                              daySlots.map((slot) => {
                                const hasConf = conflictIds.has(slot.id);
                                return (
                                  <div key={slot.id} style={{
                                    backgroundColor: hasConf ? 'rgba(248,113,113,0.15)' : 'rgba(255,255,255,0.06)',
                                    border: `1px solid ${hasConf ? 'rgba(248,113,113,0.5)' : colors.neutral.gray800}`,
                                    borderRadius: borderRadius.sm, padding: '4px 6px', marginBottom: '2px',
                                    position: 'relative',
                                  }}>
                                    {hasConf && (
                                      <span style={{ position: 'absolute', top: '2px', right: '4px', color: '#f87171', fontSize: '10px' }}>⚠</span>
                                    )}
                                    <p style={{ margin: 0, fontSize: '10px', fontWeight: typography.fontWeight.semibold, color: hasConf ? '#f87171' : colors.neutral.white }}>
                                      {slot.class_name ?? slot.class_id.slice(0, 8)}
                                    </p>
                                    {slot.room_code && <p style={{ margin: 0, fontSize: '9px', color: colors.neutral.gray400 }}>{slot.room_code}</p>}
                                    {slot.is_substitute && <p style={{ margin: 0, fontSize: '9px', color: colors.primary.yellow }}>{UI.substituteLabel}</p>}
                                    {isAdmin && (
                                      <button
                                        onClick={() => void handleDeleteSlot(slot.id)}
                                        disabled={deletingId === slot.id}
                                        style={{ position: 'absolute', bottom: '2px', right: '4px', background: 'none', border: 'none', color: colors.neutral.gray600, cursor: 'pointer', fontSize: '10px', lineHeight: 1 }}
                                        title="Padam slot"
                                      >
                                        {deletingId === slot.id ? '…' : '×'}
                                      </button>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Conflict list */}
          {conflicts.length > 0 && (
            <div style={{ marginTop: spacing.xl, backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.xl, padding: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontWeight: typography.fontWeight.semibold }}>
                ⚠ {conflicts.length} {UI.conflictSectionTitle}
              </p>
              {conflicts.map((c, i) => (
                <p key={i} style={{ margin: `0 0 ${spacing.xs}`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                  <strong style={{ color: '#f87171' }}>{c.type}</strong> — {c.detail} (Slot: {c.slotId.slice(0, 8)}…)
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
function thStyle(): React.CSSProperties {
  return {
    backgroundColor: colors.neutral.charcoal, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
    color: colors.neutral.gray400, textAlign: 'left',
    border: `1px solid ${colors.neutral.gray800}`, whiteSpace: 'nowrap',
  };
}
function tdStyle(): React.CSSProperties {
  return {
    padding: `${spacing.xs} ${spacing.sm}`, verticalAlign: 'top',
    border: `1px solid ${colors.neutral.gray800}`, minWidth: '100px',
    fontSize: typography.fontSize.xs,
  };
}
